import { createSampleFormPayload, FormModel, type FormEnforcements, type PatchForm } from '@/models/form';
import FormService from '@/services/formService';
import { defineStore } from 'pinia';
import { reactive, ref, computed } from 'vue';
import { useStudyStore } from './study';
import { TableColumnType, type TableColumnModel } from '@/components/common/table/interfaces/TableColumnModel';
import { LoadingStateModel } from '@/models/loadingState';
import { useRoute, useRouter } from 'vue-router';

export const useFormStore = defineStore('form', () => {

  const tableColumns = computed(() => {
    const columns: TableColumnModel[] = [
      {
        id: 1,
        header: 'Order',
        key: 'order',
        type: TableColumnType.USER,
        sortable: true
      },
      {
        id: 2,
        header: 'Title',
        type: TableColumnType.TEXT_WTIH_ICON,
        key: 'title',
        sortable: true,
        icon: {
          icon: 'file',
          style: 'fas',
          color: 'hsl(263, 66.3%, 36.1%)'
        }
      },
      {
        id: 3,
        header: 'Creation date',
        type: TableColumnType.TEXT,
        key: 'createdAt',
        sortable: true
      },
      {
        id: 4,
        header: 'Last edited',
        type: TableColumnType.TEXT,
        key: 'updatedAt',
        sortable: true
      },
      {
        id: 5,
        header: 'Fields',
        type: TableColumnType.TEXT,
        key: 'fieldCount',
        sortable: false
      },
      ...(studyStore.selectedStudy?.flags?.enableEntryMetrics ? [{
        id: 7,
        header: 'Average entry time',
        type: TableColumnType.TEXT,
        key: 'averageEntryTime',
        sortable: false
      }] : []),
      {
        id: 5,
        header: '',
        type: TableColumnType.SQAURE_BUTTON_WITH_ICON,
        key: 'open',
        sortable: false
      },
      {
        id: 5,
        header: '',
        type: TableColumnType.SQAURE_BUTTON_WITH_ICON,
        key: 'clone',
        sortable: false
      },
      {
        id: 6,
        header: '',
        type: TableColumnType.SQAURE_BUTTON_WITH_ICON,
        key: 'print',
        sortable: false
      }
    ];

    return columns;
  });

  const studyStore = useStudyStore();
  const formList = ref<FormModel[]>([]);
  const currentForm = ref<FormModel>();
  const route = useRoute();

  const isGettingForms = reactive(new LoadingStateModel({isLoading: false}));
  const getStudyForms = async () => {
    if (studyStore.selectedStudyId && !isGettingForms.isLoading) {
      try {
        formList.value = [];
        isGettingForms.startLoading();
        formList.value = await FormService.getStudyForms(studyStore.selectedStudyId, route.params.identifier);
        formList.value = formList.value.sort((a, b) => a.order! - b.order!);
      } catch (error) {
        isGettingForms.setError(error);
      } finally {
        isGettingForms.stopLoadingAndFinish();
      }
    }
  };


  const deleteFormLoader = reactive(new LoadingStateModel({isLoading: false}));
  const deleteForms = async (selectedForms: FormModel[]) => {
    deleteFormLoader.startLoading();
    try {
      for (const form of selectedForms) {
        await FormService.deleteForm(form.identifier);
      }
      if (selectedForms.length > 1) {
        deleteFormLoader.setSuccess(`${selectedForms.length} forms deleted!`);
      } else {
        deleteFormLoader.setSuccess(`${selectedForms[0].title} form deleted!`);
      }
    } catch (error) {
      deleteFormLoader.setError(error);
    } finally {
      deleteFormLoader.stopLoadingAndFinish();
    }
  };

  const cloneFormLoader = reactive(new LoadingStateModel({isLoading: false}));
  const cloneForms = async (form: FormModel) => {
    cloneFormLoader.startLoading();
    try {
      await FormService.cloneForm(form.identifier);
      getStudyForms();
      cloneFormLoader.setSuccess(`${form.title} cloned!`);
    } catch (error) {
      cloneFormLoader.setError(error);
    } finally {
      cloneFormLoader.stopLoadingAndFinish();
    }
  };

  
  const formsOrderLoader = reactive(new LoadingStateModel({isLoading: false}));
  const updateFormsOrder = async (forms: FormModel[]) => {
    formsOrderLoader.startLoading();
    const patchForms: PatchForm[] = [];
    forms.forEach(form => {
      const enforcement: FormEnforcements = {...form.enforcements};
      patchForms.push({
        surveyIdentifier: form.identifier,
        enforcements: enforcement
      });
    });

    if (studyStore.selectedStudyId) {
      try {
        await FormService.patchFormsOrder({
          investigationId: studyStore.selectedStudyId,
          surveys: patchForms
        });
        formsOrderLoader.setSuccess(`Forms enforcements applied!`);
      } catch (error) {
        formsOrderLoader.setError(error);
      } finally {
        formsOrderLoader.stopLoadingAndFinish();
      }
    }
  };


  const router = useRouter();
  const createFormLoader = reactive(new LoadingStateModel({isLoading: false}));
  const postForm = async () => {
    createFormLoader.startLoading();
    try {
      const postFormResponse = await FormService.postForm(createSampleFormPayload(studyStore.selectedStudyId!));
      getStudyForms();
      router.push(`form/${postFormResponse.identifier}`);
    } catch (error) {
      createFormLoader.setError(error);
    } finally {
      createFormLoader.stopLoadingAndFinish();
    }
  };

  const getForm = async (formId: string) => {
    isGettingForms.startLoading();
    try {
      currentForm.value = await FormService.getForm(formId);
    } catch (error) {
      isGettingForms.setError(error);
    } finally {
      isGettingForms.stopLoadingAndFinish();
    }
  };

  return {
    getStudyForms,
    deleteForms,
    cloneForms,
    updateFormsOrder,
    postForm,
    getForm,
    formList,
    tableColumns,
    isGettingForms,
    deleteFormLoader,
    cloneFormLoader,
    formsOrderLoader,
    createFormLoader,
    currentForm
  };
});
